import React from "react";
import {
  Box,
  Card,
  Checkbox,
  Chip,
  Typography,
} from "@mui/material";
import {
  formatOriginWithInvoices,
  formatScheduledDate,
  getRouteName,
  getShipmentLoads,
} from "./kanbanDisplay";

interface DeliveryPickingCardProps {
  item: any;
  stageColor?: string;
  userTimezone?: string;
  isSelected: boolean;
  onToggle: (itemId: number, checked: boolean) => void;
  dragListeners?: Record<string, unknown>;
  isDragSource?: boolean;
}

export const DeliveryPickingCard = React.memo(function DeliveryPickingCard({
  item,
  stageColor = "#808080",
  userTimezone,
  isSelected,
  onToggle,
  dragListeners,
  isDragSource = false,
}: DeliveryPickingCardProps) {
  const loads = getShipmentLoads(item);
  const routeName = getRouteName(item);
  const driverName = item?.driver_id?.name || "";
  const originLine = formatOriginWithInvoices(item);
  const hasAssignment = Boolean(routeName || driverName);

  return (
    <Card
      variant="outlined"
      {...dragListeners}
      sx={{
        mb: 1.5,
        borderColor: isSelected ? "#7ec597" : "#e5e9ef",
        bgcolor: isSelected ? "#f7fcf9" : "#fff",
        borderRadius: "12px",
        boxShadow: isSelected
          ? "0 0 0 1px rgba(126, 197, 151, 0.35)"
          : "0 1px 2px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        cursor: dragListeners ? (isDragSource ? "grabbing" : "grab") : "default",
        "&:hover": {
          borderColor: "#c8d8cf",
          boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
        },
      }}
    >
      <Box sx={{ display: "flex", minHeight: "100%" }}>
        <Box
          sx={{
            width: 4,
            flexShrink: 0,
            bgcolor: stageColor,
          }}
        />
        <Box sx={{ flex: 1, p: 1.75, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#1f2937",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {item?.name}
              </Typography>
              {item?.scheduled_date && (
                <Typography
                  variant="caption"
                  sx={{ color: "#6b7280", display: "block", mt: 0.35 }}
                >
                  {formatScheduledDate(item.scheduled_date, userTimezone)}
                </Typography>
              )}
            </Box>
            <Checkbox
              checked={isSelected}
              onChange={(_event, checked) => onToggle(item.id, checked)}
              onPointerDown={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              sx={{
                p: 0.25,
                mt: -0.25,
                color: "#9ca3af",
                "&.Mui-checked": { color: "#5fa67a" },
              }}
            />
          </Box>

          {item?.partner_id?.name && (
            <Typography
              variant="body2"
              sx={{
                color: "#374151",
                fontWeight: 600,
                mb: 0.5,
                lineHeight: 1.35,
              }}
            >
              {item.partner_id.name}
            </Typography>
          )}

          {originLine && (
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                display: "block",
                lineHeight: 1.45,
                wordBreak: "break-word",
              }}
            >
              {originLine}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
              mt: 1.25,
            }}
          >
            {loads > 0 && (
              <Chip
                size="small"
                label={`${loads} load${loads === 1 ? "" : "s"}`}
                sx={{
                  height: 24,
                  bgcolor: "#eef7f1",
                  color: "#3d6b52",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                }}
              />
            )}
            {item?.state && (
              <Chip
                size="small"
                label={item.state}
                sx={{
                  height: 24,
                  bgcolor: "#f3f4f6",
                  color: "#4b5563",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  textTransform: "capitalize",
                }}
              />
            )}
          </Box>

          {hasAssignment && (
            <Box
              sx={{
                mt: 1.25,
                pt: 1.25,
                borderTop: "1px solid #eef1f4",
                display: "grid",
                gap: 0.35,
              }}
            >
              {routeName && (
                <Box sx={{ display: "flex", gap: 0.75, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#9ca3af", minWidth: 42, flexShrink: 0 }}
                  >
                    Route
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#374151", fontWeight: 600, wordBreak: "break-word" }}
                  >
                    {routeName}
                  </Typography>
                </Box>
              )}
              {driverName && (
                <Box sx={{ display: "flex", gap: 0.75, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: "#9ca3af", minWidth: 42, flexShrink: 0 }}
                  >
                    Driver
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#374151", fontWeight: 600, wordBreak: "break-word" }}
                  >
                    {driverName}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
});
